import { useState } from 'react';

import { AdminSolarDateField } from '../components/admin-date-field.js';
import { normalizeAdminCode } from '../components/form.js';
import { Button, Checkbox, NativeSelect, PercentField, TextField } from '../kit.js';
import type { MarketingBaseType, MerchantMarketingSetup } from '../marketing-types.js';
import { pageRows, useMarketingRecipients, useMarketingStructures } from '../queries.js';

const CODE = /^[A-Z0-9][A-Z0-9-]{2,31}$/;

export interface MarketingLayerDraft {
  readonly baseType: MarketingBaseType;
  readonly percentage: string;
  readonly recipientId: string;
  readonly roleLabelFa: string;
}

export interface MarketingSetupDraft {
  readonly assignees: readonly string[];
  readonly code: string;
  readonly effectiveFrom: string;
  readonly effectiveTo: string;
  readonly enabled: boolean;
  readonly layers: readonly MarketingLayerDraft[];
  readonly mode: 'new' | 'published';
  readonly nameFa: string;
  readonly publishedLayerCount: number;
  readonly structureVersionId: string;
}

export function emptyMarketingSetup(): MarketingSetupDraft {
  return {
    assignees: [],
    code: '',
    effectiveFrom: '',
    effectiveTo: '',
    enabled: false,
    layers: [{ baseType: 'PURCHASE_AMOUNT', percentage: '', recipientId: '', roleLabelFa: '' }],
    mode: 'published',
    nameFa: '',
    publishedLayerCount: 0,
    structureVersionId: '',
  };
}

/** A problem with the optional block, or undefined when it can be omitted or sent. */
export function marketingSetupError(draft: MarketingSetupDraft): string | undefined {
  if (!draft.enabled) return undefined;
  if (draft.effectiveFrom === '') return 'تاریخ شروع بازاریابی را انتخاب کنید.';
  if (draft.mode === 'published') {
    if (draft.structureVersionId === '') return 'ساختار منتشرشده را انتخاب کنید.';
    return undefined;
  }
  if (!CODE.test(draft.code.trim())) return 'کد ساختار را با حرف یا عدد انگلیسی شروع کنید.';
  if (draft.nameFa.trim().length < 2) return 'نام ساختار را بنویسید.';
  if (draft.layers.length < 1 || draft.layers.length > 7) return 'ساختار یک تا هفت لایه دارد.';
  if (
    draft.layers.some((layer) => layer.roleLabelFa.trim() === '' || layer.percentage.trim() === '')
  ) {
    return 'هر لایه نام نقش و درصد می‌خواهد.';
  }
  return undefined;
}

export function toMerchantMarketingSetup(
  draft: MarketingSetupDraft,
): MerchantMarketingSetup | undefined {
  if (!draft.enabled || marketingSetupError(draft) !== undefined) return undefined;
  const count = draft.mode === 'published' ? draft.publishedLayerCount : draft.layers.length;
  const recipients =
    draft.mode === 'published' ? draft.assignees : draft.layers.map((layer) => layer.recipientId);
  return {
    effectiveFrom: draft.effectiveFrom,
    layerAssignees: Array.from({ length: count }, (_, index) => {
      const recipientId = recipients[index] ?? '';
      return {
        layerNumber: index + 1,
        ...(recipientId === '' ? {} : { marketingRecipientId: recipientId }),
      };
    }),
    structure:
      draft.mode === 'published'
        ? { structureVersionId: draft.structureVersionId }
        : {
            newStructure: {
              code: draft.code.trim(),
              layers: draft.layers.map((layer, index) => ({
                baseType: layer.baseType,
                layerNumber: index + 1,
                percentage: layer.percentage.trim(),
                roleLabelFa: layer.roleLabelFa.trim(),
              })),
              nameFa: draft.nameFa.trim(),
            },
          },
    ...(draft.effectiveTo === '' ? {} : { effectiveTo: draft.effectiveTo }),
  };
}

/** Optional Super-Admin marketing block on merchant registration (AC §28.13). Off by default. */
export function MerchantMarketingSetupFields({
  draft,
  error,
  onChange,
}: {
  readonly draft: MarketingSetupDraft;
  readonly error?: string;
  readonly onChange: (next: MarketingSetupDraft) => void;
}) {
  const [opened, setOpened] = useState(draft.enabled);
  const structures = useMarketingStructures({}, opened);
  const recipients = useMarketingRecipients({ status: 'ACTIVE' }, opened);
  const published = pageRows(structures.data).filter((item) => item.publishedVersion !== null);
  const people = pageRows(recipients.data);

  function patch(next: Partial<MarketingSetupDraft>) {
    onChange({ ...draft, ...next });
  }

  return (
    <fieldset className="admin-fieldset">
      <legend>بازاریابی (اختیاری)</legend>
      <Checkbox
        checked={draft.enabled}
        label="اتصال بازاریابی هنگام ثبت فروشگاه"
        onChange={(event) => {
          const enabled = event.target.checked;
          setOpened(enabled);
          patch({ enabled });
        }}
      />
      {error === undefined ? null : (
        <p className="saay-field__error" role="alert">
          {error}
        </p>
      )}
      {draft.enabled ? (
        <>
          <p className="admin-muted">
            اگر خالی بماند، فروشگاه بدون اتصال بازاریابی ثبت می‌شود. این بخش فقط با همان درخواست
            فروشگاه فرستاده می‌شود.
          </p>
          <div className="flex gap-2" role="group" aria-label="نوع ساختار">
            <Button
              onClick={() => patch({ mode: 'published' })}
              type="button"
              variant={draft.mode === 'published' ? 'primary' : 'secondary'}
            >
              ساختار موجود
            </Button>
            <Button
              onClick={() => patch({ mode: 'new' })}
              type="button"
              variant={draft.mode === 'new' ? 'primary' : 'secondary'}
            >
              ساختار تازه
            </Button>
          </div>
          {draft.mode === 'published' ? (
            <NativeSelect
              label="ساختار منتشرشده"
              onChange={(event) => {
                const versionId = event.target.value;
                const structure = published.find((item) => item.publishedVersion?.id === versionId);
                const count = structure?.publishedVersion?.layerCount ?? 0;
                patch({
                  assignees: Array.from({ length: count }, () => ''),
                  publishedLayerCount: count,
                  structureVersionId: versionId,
                });
              }}
              options={published.map((structure) => ({
                label: structure.nameFa,
                value: structure.publishedVersion?.id ?? '',
              }))}
              placeholder="انتخاب کنید"
              value={draft.structureVersionId}
            />
          ) : (
            <>
              <TextField
                label="کد ساختار"
                ltr
                onChange={(event) => patch({ code: normalizeAdminCode(event.target.value) })}
                value={draft.code}
              />
              <TextField
                label="نام ساختار"
                onChange={(event) => patch({ nameFa: event.target.value })}
                value={draft.nameFa}
              />
              {draft.layers.map((layer, index) => (
                <div className="flex flex-col gap-2" key={index}>
                  <TextField
                    label={`نقش لایه ${index + 1}`}
                    onChange={(event) => {
                      const layers = draft.layers.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, roleLabelFa: event.target.value } : item,
                      );
                      patch({ layers });
                    }}
                    value={layer.roleLabelFa}
                  />
                  <NativeSelect
                    label="پایه"
                    onChange={(event) => {
                      const baseType = event.target.value as MarketingBaseType;
                      const layers = draft.layers.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, baseType } : item,
                      );
                      patch({ layers });
                    }}
                    options={[
                      { label: 'مبلغ خرید', value: 'PURCHASE_AMOUNT' },
                      { label: 'سهم کارمزد', value: 'SAAY_FEE' },
                    ]}
                    value={layer.baseType}
                  />
                  <PercentField
                    label="درصد"
                    onChangeRate={(rate) => {
                      const layers = draft.layers.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, percentage: rate ?? '' } : item,
                      );
                      patch({ layers });
                    }}
                    valueRate={layer.percentage === '' ? null : layer.percentage}
                  />
                </div>
              ))}
              {draft.layers.length < 7 ? (
                <Button
                  onClick={() =>
                    patch({
                      layers: [
                        ...draft.layers,
                        {
                          baseType: 'PURCHASE_AMOUNT',
                          percentage: '',
                          recipientId: '',
                          roleLabelFa: '',
                        },
                      ],
                    })
                  }
                  type="button"
                  variant="secondary"
                >
                  لایهٔ دیگر
                </Button>
              ) : null}
            </>
          )}
          <AdminSolarDateField
            label="شروع اتصال"
            onChange={(value) => patch({ effectiveFrom: value })}
            value={draft.effectiveFrom}
          />
          <AssigneeFields
            count={draft.mode === 'published' ? draft.publishedLayerCount : draft.layers.length}
            onChange={(index, recipientId) => {
              if (draft.mode === 'published') {
                const assignees = draft.assignees.map((item, itemIndex) =>
                  itemIndex === index ? recipientId : item,
                );
                patch({ assignees });
                return;
              }
              const layers = draft.layers.map((item, itemIndex) =>
                itemIndex === index ? { ...item, recipientId } : item,
              );
              patch({ layers });
            }}
            people={people.map((recipient) => ({
              label: recipient.user.displayNameFa ?? recipient.recipientCode,
              value: recipient.id,
            }))}
            values={
              draft.mode === 'published'
                ? draft.assignees
                : draft.layers.map((layer) => layer.recipientId)
            }
          />
        </>
      ) : null}
    </fieldset>
  );
}

function AssigneeFields({
  count,
  onChange,
  people,
  values,
}: {
  readonly count: number;
  readonly onChange: (index: number, recipientId: string) => void;
  readonly people: readonly { readonly label: string; readonly value: string }[];
  readonly values: readonly string[];
}) {
  if (count === 0) return null;
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <NativeSelect
          key={index}
          label={`بازاریاب لایه ${index + 1} (اختیاری)`}
          onChange={(event) => onChange(index, event.target.value)}
          options={people}
          placeholder="بدون بازاریاب"
          value={values[index] ?? ''}
        />
      ))}
    </>
  );
}
